// src/services/FirebaseService.js
import { 
    collection, 
    doc,
    addDoc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    writeBatch
  } from 'firebase/firestore';
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL, 
    deleteObject 
  } from 'firebase/storage';
  import { db, storage } from '../firebase/firebase';
  
  /**
   * Service for managing cases and PDFs with the new data structure
   */
  const FirebaseService = {
    /**
     * Create a new case
     * @param {Object} caseData - Basic case data
     * @param {String} userId - User ID who owns the case
     * @returns {Promise<Object>} - Promise resolving to the complete case object
     */
    async createCase(caseData, userId) {
      const existingCases = await this.getUserCases(userId);
      const currentOrder = existingCases.length;

      try {
        if (!userId) {
          throw new Error('User ID is required');
        }
        
        // Prepare case data with timestamp placeholders
        const newCaseData = {
          ...caseData,
          userId,
          pdfCount: 0,
          order: currentOrder,
        };
        
        // Create case document
        const caseRef = await addDoc(collection(db, 'cases'), {
          ...newCaseData,
          createdAt: serverTimestamp(),
          lastModified: serverTimestamp(),
        });
        
        console.log('Created case:', caseRef.id);
        
        // Return the complete case object with the generated ID
        const completeCase = {
          id: caseRef.id,
          ...newCaseData,
          createdAt: new Date(), // Use current date for immediate display
          lastModified: new Date(),
        };
        
        return completeCase;
      } catch (error) {
        console.error('Error creating case:', error);
        throw error;
      }
    },
    
    /**
     * Get all cases for a user
     * @param {String} userId - User ID
     * @returns {Promise<Array>} - Promise resolving to array of cases
     */
    async getUserCases(userId) {
      try {
        if (!userId) {
          throw new Error('User ID is required');
        }
        
        // Query for user's cases
        const q = query(
          collection(db, 'cases'),
          where('userId', '==', userId),
          orderBy('order', 'asc'),
          orderBy('lastModified', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        
        // Map to array
        const cases = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          cases.push({
            id: doc.id,
            name: data.name || 'Untitled Case',
            caseNumber: data.caseNumber || '',
            status: data.status || 'active',
            order: data.order || 0,
            createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
            lastModified: data.lastModified ? data.lastModified.toDate() : new Date(),
            pdfCount: data.pdfCount || 0,
            stampConfig: data.stampConfig || null,
            archived: data.archived || false,
            documentSets: data.documentSets || [],
          });
        });
        
        return cases;
      } catch (error) {
        console.error('Error getting user cases:', error);
        throw error;
      }
    },
    
    /**
     * Update a case
     * @param {String} caseId - Case ID to update
     * @param {Object} updates - Fields to update
     * @param {String} userId - User ID (for authorization)
     * @returns {Promise<void>}
     */
    async updateCase(caseId, updates, userId) {
      try {
        if (!caseId || !userId) {
          throw new Error('Case ID and User ID are required');
        }
        
        const caseRef = doc(db, 'cases', caseId);
        
        // Add lastModified timestamp
        const updateData = {
          ...updates,
          lastModified: serverTimestamp(),
        };
        
        await updateDoc(caseRef, updateData);
        console.log('Case updated:', caseId);
      } catch (error) {
        console.error('Error updating case:', error);
        throw error;
      }
    },
    
    /**
     * Delete a case and all its associated files
     * @param {String} caseId - Case ID to delete
     * @param {String} userId - User ID (for authorization)
     * @returns {Promise<void>}
     */
    async deleteCase(caseId, userId) {
      try {
        if (!caseId) {
          throw new Error('Case ID is required');
        }
        
        // Get the case to verify ownership
        const caseRef = doc(db, 'cases', caseId);
        const caseSnap = await getDoc(caseRef);
        
        if (!caseSnap.exists()) {
          throw new Error('Case not found');
        }
        
        const caseData = caseSnap.data();
        
        // Verify ownership if userId provided
        if (userId && caseData.userId !== userId) {
          throw new Error('You do not have permission to delete this case');
        }
        
        // Get all PDFs for this case using the CORRECT subcollection structure
        const pdfsSnapshot = await getDocs(collection(db, 'cases', caseId, 'pdfs'));
        
        // Delete each PDF and its storage file
        const batch = writeBatch(db);
        const storageDeletePromises = [];
        
        pdfsSnapshot.forEach(pdfDoc => {
          const pdfData = pdfDoc.data();
          
          // Add PDF document to batch delete
          const pdfRef = doc(db, 'cases', caseId, 'pdfs', pdfDoc.id);
          batch.delete(pdfRef);
          
          // Add storage file deletion if path exists
          if (pdfData.storagePath) {
            const storageRef = ref(storage, pdfData.storagePath);
            storageDeletePromises.push(deleteObject(storageRef));
          }
        });
        
        // Execute batch delete of PDF documents
        await batch.commit();
        
        // Delete storage files (continue even if some fail)
        await Promise.allSettled(storageDeletePromises);
        
        // Finally, delete the case document
        await deleteDoc(caseRef);
        
        console.log('Case deleted successfully:', caseId);
      } catch (error) {
        console.error('Error deleting case:', error);
        throw error;
      }
    },

    /**
     * Get all PDFs for a case
     * @param {String} caseId - Case ID
     * @returns {Promise<Array>} - Promise resolving to array of PDFs
     */
    async getCasePDFs(caseId) {
      try {
        if (!caseId) {
          throw new Error('Case ID is required');
        }
        
        // Query for case PDFs
        const q = query(
          collection(db, 'pdfs'),
          where('caseId', '==', caseId),
          orderBy('order', 'asc'),
          orderBy('uploadedAt', 'asc')
        );
        
        const querySnapshot = await getDocs(q);
        
        // Map to array
        const pdfs = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          pdfs.push({
            id: doc.id,
            ...data,
            uploadedAt: data.uploadedAt ? data.uploadedAt.toDate() : new Date(),
            lastModified: data.lastModified ? data.lastModified.toDate() : new Date(),
          });
        });
        
        return pdfs;
      } catch (error) {
        console.error('Error getting case PDFs:', error);
        throw error;
      }
    },

    /**
     * Upload a PDF file
     * @param {File} file - File to upload
     * @param {String} caseId - Case ID
     * @param {String} userId - User ID
     * @param {Object} pdfData - Additional PDF metadata
     * @returns {Promise<Object>} - Promise resolving to PDF data
     */
    async uploadPDF(file, caseId, userId, pdfData = {}) {
      try {
        if (!file || !caseId || !userId) {
          throw new Error('File, case ID, and user ID are required');
        }
        
        // Generate unique filename
        const fileId = Date.now().toString() + '_' + Math.random().toString(36).substring(7);
        const filename = `${fileId}_${file.name}`;
        const storagePath = `uploads/${userId}/${caseId}/${filename}`;
        
        // Upload file
        const storageRef = ref(storage, storagePath);
        const uploadResult = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(uploadResult.ref);
        
        // Prepare PDF document data
        const newPDF = {
          caseId,
          userId,
          originalName: file.name,
          filename,
          pdfPath: storagePath,
          pdfUrl: downloadURL,
          fileSize: file.size,
          fileType: file.type,
          uploadedAt: serverTimestamp(),
          lastModified: serverTimestamp(),
          ...pdfData,
        };
        
        // Save to Firestore
        const pdfRef = await addDoc(collection(db, 'pdfs'), newPDF);
        
        console.log('PDF uploaded:', pdfRef.id);
        
        // Update case PDF count
        const casePDFs = await this.getCasePDFs(caseId);
        await this.updateCase(caseId, { pdfCount: casePDFs.length + 1 }, userId);
        
        return {
          id: pdfRef.id,
          ...newPDF,
          uploadedAt: new Date(),
          lastModified: new Date(),
        };
      } catch (error) {
        console.error('Error uploading PDF:', error);
        throw error;
      }
    },

    /**
     * Update a PDF document
     * @param {String} pdfId - PDF ID
     * @param {Object} updates - Fields to update
     * @param {String} userId - User ID (for authorization)
     * @returns {Promise<void>}
     */
    async updatePDF(pdfId, updates, userId) {
      try {
        if (!pdfId || !userId) {
          throw new Error('PDF ID and User ID are required');
        }
        
        const pdfRef = doc(db, 'pdfs', pdfId);
        
        const updateData = {
          ...updates,
          lastModified: serverTimestamp(),
        };
        
        await updateDoc(pdfRef, updateData);
        console.log('PDF updated:', pdfId);
      } catch (error) {
        console.error('Error updating PDF:', error);
        throw error;
      }
    },

    /**
     * Delete a PDF document and its file
     * @param {String} pdfId - PDF ID
     * @param {String} userId - User ID (for authorization)
     * @returns {Promise<void>}
     */
    async deletePDF(pdfId, userId) {
      try {
        if (!pdfId || !userId) {
          throw new Error('PDF ID and User ID are required');
        }
        
        // Get PDF data first
        const pdfRef = doc(db, 'pdfs', pdfId);
        const pdfDoc = await getDoc(pdfRef);
        
        if (!pdfDoc.exists()) {
          throw new Error('PDF not found');
        }
        
        const pdfData = pdfDoc.data();
        
        // Delete file from storage
        if (pdfData.pdfPath) {
          try {
            const fileRef = ref(storage, pdfData.pdfPath);
            await deleteObject(fileRef);
          } catch (error) {
            console.warn('Error deleting file from storage:', error);
          }
        }
        
        // Delete document
        await deleteDoc(pdfRef);
        
        // Update case PDF count
        const casePDFs = await this.getCasePDFs(pdfData.caseId);
        await this.updateCase(pdfData.caseId, { pdfCount: casePDFs.length }, userId);
        
        console.log('PDF deleted:', pdfId);
      } catch (error) {
        console.error('Error deleting PDF:', error);
        throw error;
      }
    },

    /**
     * Reorder PDFs in a case
     * @param {String} caseId - Case ID
     * @param {Array} orderUpdates - Array of {pdfId, order} objects
     * @param {String} userId - User ID (for authorization)
     * @returns {Promise<void>}
     */
    async reorderPDFs(caseId, orderUpdates, userId) {
      try {
        if (!caseId || !userId || !Array.isArray(orderUpdates)) {
          throw new Error('Case ID, user ID, and order updates array are required');
        }

        const batch = writeBatch(db);

        for (const update of orderUpdates) {
          const pdfRef = doc(db, 'pdfs', update.pdfId);
          batch.update(pdfRef, {
            order: update.order,
            lastModified: serverTimestamp()
          });
        }

        await batch.commit();
        console.log('PDFs reordered for case:', caseId);
      } catch (error) {
        console.error('Error reordering PDFs:', error);
        throw error;
      }
    },
  };

  export default FirebaseService;